import { resolveVendorDirectoryApiBase } from "./clientPortalFetch.js";

function authHeaders() {
  return { "Content-Type": "application/json" };
}

function base(clientViewId) {
  return `${resolveVendorDirectoryApiBase(clientViewId)}`;
}

async function parse(response, fallback) {
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || fallback);
  return body;
}

export async function getRankingDashboard(clientViewId) {
  const response = await fetch(`${base(clientViewId)}/ranking/panel`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load the ranking dashboard.");
}

export async function listRankingModels(clientViewId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/ranking/modelos?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load ranking models.");
}

export async function getRankingModel(clientViewId, modelId) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load this ranking model.");
}

export async function cloneRankingModel(clientViewId, modelId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/clonar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to clone the ranking model.");
}

export async function updateRankingModel(clientViewId, modelId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}`, {
    method: "PATCH",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to update the ranking model.");
}

export async function validateRankingModel(clientViewId, modelId) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/validar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to validate the ranking model.");
}

export async function moveRankingModelToTesting(clientViewId, modelId) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/probar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to move the model to testing.");
}

export async function previewRankingModelActivation(clientViewId, modelId) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/activacion-preview`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load the activation preview.");
}

export async function activateRankingModel(clientViewId, modelId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/activar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to activate the ranking model.");
}

export async function retireRankingModel(clientViewId, modelId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/modelos/${modelId}/retirar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to retire the ranking model.");
}

export async function listRankingRuns(clientViewId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/ranking/ejecuciones?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load ranking runs.");
}

export async function getRankingRun(clientViewId, runId) {
  const response = await fetch(`${base(clientViewId)}/ranking/ejecuciones/${runId}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load this ranking run.");
}

export async function startRankingRun(clientViewId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/ejecuciones`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to start the ranking run.");
}

export async function listRankingSimulations(clientViewId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/ranking/simulaciones?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load simulations.");
}

export async function createRankingSimulation(clientViewId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/simulaciones`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to create the simulation.");
}

export async function getRankingSimulation(clientViewId, simulationId) {
  const response = await fetch(`${base(clientViewId)}/ranking/simulaciones/${simulationId}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load this simulation.");
}

export async function listRankingOverrides(clientViewId) {
  const response = await fetch(`${base(clientViewId)}/ranking/overrides`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load exclusions.");
}

export async function createRankingOverride(clientViewId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/overrides`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to create the exclusion.");
}

export async function revokeRankingOverride(clientViewId, overrideId, payload = {}) {
  const response = await fetch(`${base(clientViewId)}/ranking/overrides/${overrideId}/revocar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to revoke the exclusion.");
}

export async function getVendorRankingExplanation(clientViewId, vendorId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/proveedores/${vendorId}/ranking/explicacion?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load the ranking explanation.");
}

export async function listPlacements(clientViewId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/ranking/colocaciones?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load placements.");
}

export async function createPlacement(clientViewId, payload) {
  const response = await fetch(`${base(clientViewId)}/ranking/colocaciones`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to create the placement.");
}

export async function cancelPlacement(clientViewId, placementId, payload = {}) {
  const response = await fetch(`${base(clientViewId)}/ranking/colocaciones/${placementId}/cancelar`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parse(response, "Unable to cancel the placement.");
}

export async function previewPublicResults(clientViewId, query = {}) {
  const params = new URLSearchParams(query);
  const response = await fetch(`${base(clientViewId)}/ranking/preview-resultados?${params}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to generate the preview.");
}

export async function getRankingMethodology(clientViewId) {
  const response = await fetch(`${base(clientViewId)}/ranking/metodologia`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return parse(response, "Unable to load the methodology.");
}
